import {
  Button,
  Input,
  Label,
  PageHeader,
  SecretInput,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Spinner,
  StatusBadge,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@repo/ui'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { ArrowLeft, Plug, Target, Trash2, Wand2 } from 'lucide-react'
import { type FormEvent, useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'

import { useAuth } from '@/hooks/use-auth'
import {
  useConfigureExactSpotter,
  useCustomFields,
  useDisconnectExactSpotter,
  useExactSpotterFields,
  useExactSpotterMappings,
  useExactSpotterStatus,
  useSaveExactSpotterMappings,
  useTestExactSpotter,
} from '@/hooks/use-settings'
import type { IntegrationMapping } from '@/types/settings'

export const Route = createFileRoute('/_app/integracoes/exact-spotter')({
  component: ExactSpotterPage,
})

const STANDARD_FIELDS = [
  { value: 'name', label: 'Nome' },
  { value: 'email', label: 'E-mail' },
  { value: 'phone', label: 'Telefone' },
  { value: 'company', label: 'Empresa' },
  { value: 'status', label: 'Status' },
  { value: 'source', label: 'Origem' },
]

function MappingEditor({
  rows,
  setRows,
  fieldOptions,
  detected,
  onSave,
}: {
  rows: IntegrationMapping[]
  setRows: (rows: IntegrationMapping[]) => void
  fieldOptions: Array<{ value: string; label: string }>
  detected: string[]
  onSave: () => void
}) {
  function update(index: number, key: keyof IntegrationMapping, value: string) {
    setRows(
      rows.map((row, i) => (i === index ? { ...row, [key]: value } : row))
    )
  }

  return (
    <section className="bg-card rounded-md border p-5">
      <div className="space-y-2">
        {rows.map((row, index) => (
          <div key={index} className="grid gap-2 sm:grid-cols-2">
            <div>
              <Label className="text-xs">Campo Exact</Label>
              <Input
                list="exact-fields"
                value={row.externalField}
                onChange={(event) =>
                  update(index, 'externalField', event.target.value)
                }
              />
            </div>
            <div>
              <Label className="text-xs">Campo AgroSDR</Label>
              <Select
                value={row.internalField || undefined}
                onValueChange={(value) => update(index, 'internalField', value)}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  {fieldOptions.map((field) => (
                    <SelectItem key={field.value} value={field.value}>
                      {field.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        ))}
      </div>
      <datalist id="exact-fields">
        {detected.map((field) => (
          <option key={field} value={field} />
        ))}
      </datalist>
      <div className="mt-4 flex flex-wrap gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={() =>
            setRows([...rows, { externalField: '', internalField: '' }])
          }
        >
          Adicionar campo
        </Button>
        <Button type="button" onClick={onSave}>
          Salvar
        </Button>
      </div>
    </section>
  )
}

function ExactSpotterPage() {
  const { currentOrgId } = useAuth()
  const navigate = useNavigate()
  const status = useExactSpotterStatus(currentOrgId)
  const configure = useConfigureExactSpotter(currentOrgId)
  const test = useTestExactSpotter(currentOrgId)
  const disconnect = useDisconnectExactSpotter(currentOrgId)
  const mappingsQuery = useExactSpotterMappings(currentOrgId)
  const saveMappings = useSaveExactSpotterMappings(currentOrgId)
  const customFields = useCustomFields(currentOrgId)
  const spotterFields = useExactSpotterFields(currentOrgId)

  const [apiToken, setApiToken] = useState('')
  const [baseUrl, setBaseUrl] = useState('https://api.exactsales.com.br/v3')
  const [funnelId, setFunnelId] = useState('')
  const [stepId, setStepId] = useState('')
  const [preSalesId, setPreSalesId] = useState('')
  const [inbound, setInbound] = useState<IntegrationMapping[]>([])
  const [outbound, setOutbound] = useState<IntegrationMapping[]>([])

  useEffect(() => {
    if (status.data?.baseUrl) setBaseUrl(status.data.baseUrl)
    const defaults = status.data?.defaults ?? {}
    setFunnelId(String(defaults.funnel_id ?? ''))
    setStepId(String(defaults.step_id ?? ''))
    setPreSalesId(String(defaults.pre_sales_id ?? ''))
  }, [status.data])

  useEffect(() => {
    const mappings = mappingsQuery.data ?? []
    setInbound(mappings.filter((m) => m.direction === 'inbound'))
    setOutbound(mappings.filter((m) => m.direction !== 'inbound'))
  }, [mappingsQuery.data])

  const fieldOptions = useMemo(
    () => [
      ...STANDARD_FIELDS,
      ...(customFields.data ?? []).map((field) => ({
        value: `custom:${field.key}`,
        label: `Custom: ${field.name}`,
      })),
    ],
    [customFields.data]
  )

  const detected = useMemo(() => {
    const fields = spotterFields.data?.fields ?? []
    return fields
      .map((field) => {
        if (!field || typeof field !== 'object') return null
        const value = field as Record<string, unknown>
        return String(value.name ?? value.label ?? value.key ?? '')
      })
      .filter((field): field is string => Boolean(field))
  }, [spotterFields.data?.fields])

  async function save(event: FormEvent) {
    event.preventDefault()
    await configure.mutateAsync({
      apiToken: apiToken.trim() || undefined,
      baseUrl,
      defaults: {
        funnel_id: funnelId.trim() || null,
        step_id: stepId.trim() || null,
        pre_sales_id: preSalesId.trim() || null,
      },
    })
    setApiToken('')
    toast.success('Exact Spotter salvo')
  }

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <PageHeader
        title="Exact Spotter"
        description="Configure token, defaults comerciais e mapeamento de campos."
        back={
          <Button
            type="button"
            variant="ghost"
            size="icon"
            title="Voltar para Integrações"
            onClick={() => void navigate({ to: '/integracoes' })}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
        }
        meta={
          <StatusBadge tone={status.data?.connected ? 'success' : 'neutral'}>
            {status.data?.connected ? 'Conectado' : 'Desconectado'}
          </StatusBadge>
        }
      />

      <div className="flex-1 space-y-5 overflow-y-auto p-6">
        <section className="bg-card rounded-md border p-5">
          <Target className="text-success h-8 w-8" />
          <form className="mt-4 space-y-4" onSubmit={save}>
            <div className="space-y-2">
              <Label htmlFor="exact-token">Token da API</Label>
              <SecretInput
                id="exact-token"
                visibilityLabel="token da API Exact Spotter"
                value={apiToken}
                onChange={(event) => setApiToken(event.target.value)}
                placeholder={
                  status.data?.connected
                    ? 'Deixe vazio para manter'
                    : 'Cole o token'
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="exact-base-url">Base URL</Label>
              <Input
                id="exact-base-url"
                value={baseUrl}
                onChange={(event) => setBaseUrl(event.target.value)}
              />
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="exact-funnel">Funil padrão</Label>
                <Input
                  id="exact-funnel"
                  value={funnelId}
                  onChange={(event) => setFunnelId(event.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="exact-step">Etapa padrão</Label>
                <Input
                  id="exact-step"
                  value={stepId}
                  onChange={(event) => setStepId(event.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="exact-pre-sales">Pré-vendedor</Label>
                <Input
                  id="exact-pre-sales"
                  value={preSalesId}
                  onChange={(event) => setPreSalesId(event.target.value)}
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                type="submit"
                disabled={
                  configure.isPending ||
                  (!apiToken.trim() && !status.data?.connected)
                }
              >
                {configure.isPending ? <Spinner /> : null}
                Salvar
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() =>
                  void test.mutateAsync().then((result) => {
                    if (result.ok) {
                      toast.success(`Conexão OK (${result.status})`)
                    } else {
                      toast.error(result.error ?? 'Falha ao testar')
                    }
                  })
                }
              >
                <Plug className="h-4 w-4" />
                Testar
              </Button>
              {status.data?.connected ? (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() =>
                    void disconnect
                      .mutateAsync()
                      .then(() => toast.success('Exact desconectado'))
                  }
                >
                  <Trash2 className="h-4 w-4" />
                  Desconectar
                </Button>
              ) : null}
            </div>
          </form>
        </section>

        <div>
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="font-semibold">Mapeamento de campos</h2>
              <p className="text-muted-foreground text-sm">
                Defina como campos do Spotter entram e saem do AgroSDR.
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={() =>
                void spotterFields
                  .refetch()
                  .then(() => toast.success('Campos detectados'))
              }
            >
              {spotterFields.isFetching ? (
                <Spinner />
              ) : (
                <Wand2 className="h-4 w-4" />
              )}
              Detectar campos
            </Button>
          </div>

          <Tabs defaultValue="inbound">
            <TabsList>
              <TabsTrigger value="inbound">Recebendo do Spotter</TabsTrigger>
              <TabsTrigger value="outbound">
                Enviando para o Spotter
              </TabsTrigger>
            </TabsList>
            <TabsContent value="inbound" className="mt-4">
              <MappingEditor
                rows={inbound}
                setRows={setInbound}
                fieldOptions={fieldOptions}
                detected={detected}
                onSave={() =>
                  void saveMappings
                    .mutateAsync({ direction: 'inbound', mappings: inbound })
                    .then(() => toast.success('Mapeamento inbound salvo'))
                }
              />
            </TabsContent>
            <TabsContent value="outbound" className="mt-4">
              <MappingEditor
                rows={outbound}
                setRows={setOutbound}
                fieldOptions={fieldOptions}
                detected={detected}
                onSave={() =>
                  void saveMappings
                    .mutateAsync({ direction: 'outbound', mappings: outbound })
                    .then(() => toast.success('Mapeamento outbound salvo'))
                }
              />
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  )
}
